import React, { useState, useEffect, useRef } from 'react';
import '../styles/MesTaches.css';
import { useTaches } from '../hooks/useTaches';

const MesTaches = ({ userRole = 'directeur', userName = 'Utilisateur Test', navigationParams = {} }) => {
  const {
    taches,
    loading,
    error,
    stats,
    ajouterTache,
    modifierTache,
    supprimerTache,
    toggleStatutTache,
    nettoyerTachesAnciennes,
    viderToutesLesTaches,
    calculerStatut
  } = useTaches();

  // Filtre par statut à partir des tuiles cliquables
  const [selectedStatut, setSelectedStatut] = useState(''); // '', 'a_faire', 'en_cours', 'termine', 'en_retard'
  const listRef = useRef(null);

  // Appliquer un filtre provenant d'une navigation externe
  useEffect(() => {
    if (navigationParams && navigationParams.filtreStatut) {
      setSelectedStatut(navigationParams.filtreStatut);
      // Scroll vers la liste après un petit délai pour assurer le rendu
      setTimeout(() => listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationParams]);

  const handleClickStatCard = (statut) => {
    setSelectedStatut(statut);
    // Aller directement à la liste filtrée
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    echeance: '',
    priorite: 'normal',
    categorie: 'personnel',
    statut: 'a_faire'
  });
  const [confirmDelete, setConfirmDelete] = useState({ visible: false, task: null });

  const priorites = [
    { value: 'normal', label: 'Normal', color: '#6c757d' },
    { value: 'important', label: 'Important', color: '#fd7e14' },
    { value: 'urgent', label: 'Urgent', color: '#dc3545' }
  ];

  const categories = [
    { value: 'client', label: 'Client' },
    { value: 'dossier', label: 'Dossier' },
    { value: 'admin', label: 'Administration' },
    { value: 'personnel', label: 'Personnel' }
  ];

  const statuts = [
    { value: 'a_faire', label: 'À faire', color: '#fd7e14' },
    { value: 'en_cours', label: 'En cours', color: '#0d6efd' },
    { value: 'termine', label: 'Terminé', color: '#198754' },
    { value: 'en_retard', label: 'En retard', color: '#dc3545' }
  ];

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      titre: '',
      description: '',
      echeance: '',
      priorite: 'normal',
      categorie: 'personnel',
      statut: 'a_faire'
    });
    setEditingTask(null);
  };

  const ouvrirModal = (tache = null) => {
    if (tache) {
      setFormData({
        titre: tache.titre,
        description: tache.description || '',
        echeance: tache.echeance,
        priorite: tache.priorite,
        categorie: tache.categorie,
        statut: tache.statut
      });
      setEditingTask(tache);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  // Ouvrir automatiquement le modal de création si on arrive depuis une notification
  useEffect(() => {
    if (navigationParams.ouvrirCreation) {
      ouvrirModal();
    }
  }, [navigationParams]);

  const fermerModal = () => {
    setShowModal(false);
    resetForm();
  };

  const validerFormulaire = () => {
    if (!formData.titre.trim()) {
      alert('Le titre est obligatoire');
      return false;
    }
    if (!formData.echeance) {
      alert('La date d\'échéance est obligatoire');
      return false;
    }
    return true;
  };

  const sauvegarderTache = () => {
    if (!validerFormulaire()) return;

    if (editingTask) {
      modifierTache(editingTask.id, formData);
    } else {
      ajouterTache(formData);
    }

    fermerModal();
  };

  const handleSupprimerTache = () => {
    if (confirmDelete.task) {
      supprimerTache(confirmDelete.task.id);
      setConfirmDelete({ visible: false, task: null });
    }
  };

  const handleAnnulerSuppression = () => {
    setConfirmDelete({ visible: false, task: null });
  };

  const handleNettoyerTaches = () => {
    const nbSupprimees = nettoyerTachesAnciennes();
    if (nbSupprimees > 0) {
      alert(`${nbSupprimees} tâche(s) ancienne(s) supprimée(s)`);
    } else {
      alert('Aucune tâche ancienne à supprimer');
    }
  };

  const handleViderToutesLesTaches = () => {
    if (window.confirm('⚠️ Êtes-vous sûr de vouloir supprimer TOUTES les tâches ? Cette action est irréversible.')) {
      viderToutesLesTaches();
      alert('Toutes les tâches ont été supprimées');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const maintenant = new Date();
    const diff = date - maintenant;
    const jours = Math.ceil(diff / (1000 * 60 * 60 * 24));

    const options = {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    };

    const dateFormatee = date.toLocaleDateString('fr-FR', options);

    if (jours < 0) {
      return { text: dateFormatee, status: 'retard', info: `En retard de ${Math.abs(jours)} jour(s)` };
    } else if (jours === 0) {
      return { text: dateFormatee, status: 'aujourd-hui', info: 'Aujourd\'hui' };
    } else if (jours === 1) {
      return { text: dateFormatee, status: 'demain', info: 'Demain' };
    } else {
      return { text: dateFormatee, status: 'futur', info: `Dans ${jours} jour(s)` };
    }
  };

  if (loading) {
    return (
      <div className="mes-taches-widget">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement des tâches...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mes-taches-widget">
        <div className="error-state">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mes-taches-widget">
      <div className="widget-header">
        <div className="widget-title">
          <i className="fas fa-clipboard-list"></i>
          <h3>Mes Tâches</h3>
          <span className="compteur-actives">{stats.actives}</span>
        </div>
        <button className="btn-ajouter" onClick={() => ouvrirModal()}>
          <i className="fas fa-plus"></i>
          Ajouter
        </button>
      </div>

      {/* Résumé statistiques (cliquable) */}
      <div className="stats-resume">
        <div
          className={`stat-item a-faire ${selectedStatut === 'a_faire' ? 'active' : ''}`}
          onClick={() => handleClickStatCard('a_faire')}
          role="button"
          title="Voir les tâches à faire"
          style={{ cursor: 'pointer' }}
        >
          <span className="stat-nombre">{stats.a_faire}</span>
          <span className="stat-label">À faire</span>
        </div>
        <div
          className={`stat-item en-cours ${selectedStatut === 'en_cours' ? 'active' : ''}`}
          onClick={() => handleClickStatCard('en_cours')}
          role="button"
          title="Voir les tâches en cours"
          style={{ cursor: 'pointer' }}
        >
          <span className="stat-nombre">{stats.en_cours}</span>
          <span className="stat-label">En cours</span>
        </div>
        <div
          className={`stat-item termine ${selectedStatut === 'termine' ? 'active' : ''}`}
          onClick={() => handleClickStatCard('termine')}
          role="button"
          title="Voir les tâches terminées"
          style={{ cursor: 'pointer' }}
        >
          <span className="stat-nombre">{stats.termine}</span>
          <span className="stat-label">Terminées</span>
        </div>
        <div
          className={`stat-item en-retard ${selectedStatut === 'en_retard' ? 'active' : ''}`}
          onClick={() => handleClickStatCard('en_retard')}
          role="button"
          title="Voir les tâches en retard"
          style={{ cursor: 'pointer' }}
        >
          <span className="stat-nombre">{stats.en_retard}</span>
          <span className="stat-label">En retard</span>
        </div>
      </div>

      {/* Actions rapides */}
      <div className="actions-rapides">
        {stats.actives > 20 && (
          <div className="alert-notification">
            <i className="fas fa-exclamation-triangle"></i>
            Plus de 20 tâches actives. Pensez à faire le ménage !
          </div>
        )}
        
        {stats.termine > 0 && (
          <button className="btn-nettoyer" onClick={handleNettoyerTaches}>
            <i className="fas fa-broom"></i>
            Nettoyer les anciennes
          </button>
        )}

        {taches.length > 0 && (
          <button className="btn-nettoyer" onClick={handleViderToutesLesTaches} style={{ marginLeft: '10px', backgroundColor: '#dc3545' }}>
            <i className="fas fa-trash-alt"></i>
            Vider tout
          </button>
        )}
      </div>

      {/* Bandeau filtre actif */}
      {selectedStatut && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 2px 0 2px' }}>
          <span style={{ fontSize: 13, color: '#6c757d' }}>
            Filtre: {selectedStatut === 'a_faire' ? 'À faire' : selectedStatut === 'en_cours' ? 'En cours' : selectedStatut === 'termine' ? 'Terminées' : 'En retard'}
          </span>
          <button className="btn-nettoyer" onClick={() => setSelectedStatut('')} style={{ padding: '6px 10px' }}>
            Réinitialiser
          </button>
        </div>
      )}

      {/* Liste des tâches */}
      <div ref={listRef} className="taches-liste">
        {taches.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-clipboard-check"></i>
            <p>Aucune tâche pour le moment</p>
            <button className="btn-premiere-tache" onClick={() => ouvrirModal()}>
              Créer ma première tâche
            </button>
          </div>
        ) : (
          (selectedStatut ? taches.filter(t => calculerStatut(t) === selectedStatut) : taches).map(tache => {
            const statutActuel = calculerStatut(tache);
            const dateInfo = formatDate(tache.echeance);
            const prioriteInfo = priorites.find(p => p.value === tache.priorite);
            const categorieInfo = categories.find(c => c.value === tache.categorie);

            return (
              <div key={tache.id} className={`tache-item ${statutActuel}`}>
                <div className="tache-content">
                  <div className="tache-checkbox">
                    <input
                      type="checkbox"
                      checked={statutActuel === 'termine'}
                      onChange={() => toggleStatutTache(tache.id)}
                    />
                  </div>
                  
                  <div className="tache-info">
                    <h4 className={statutActuel === 'termine' ? 'barre' : ''}>
                      {tache.titre}
                    </h4>
                    
                    {tache.description && (
                      <p className="tache-description">{tache.description}</p>
                    )}
                    
                    <div className="tache-meta">
                      <span className={`date-info ${dateInfo.status}`} title={dateInfo.info}>
                        <i className="fas fa-calendar"></i>
                        {dateInfo.text}
                      </span>
                      
                      <span className="priorite-badge" style={{ backgroundColor: prioriteInfo.color }}>
                        {prioriteInfo.label}
                      </span>
                      
                      <span className="categorie-badge">
                        {categorieInfo.label}
                      </span>
                    </div>
                  </div>
                  
                  <div className="tache-actions">
                    <button
                      className="btn-modifier"
                      onClick={() => ouvrirModal(tache)}
                      title="Modifier"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                      <button
                        className="btn-supprimer"
                        onClick={() => setConfirmDelete({ visible: true, task: tache })}
                        title="Supprimer"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal de création/édition */}
      {showModal && (
        <div className="modal-overlay" onClick={fermerModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTask ? 'Modifier la tâche' : 'Nouvelle tâche'}</h3>
              <button className="btn-fermer" onClick={fermerModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="titre">Titre *</label>
                <input
                  type="text"
                  id="titre"
                  name="titre"
                  value={formData.titre}
                  onChange={handleInputChange}
                  placeholder="Titre de la tâche"
                  maxLength="100"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Description détaillée (optionnel)"
                  rows="3"
                  maxLength="500"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="echeance">Échéance *</label>
                  <input
                    type="datetime-local"
                    id="echeance"
                    name="echeance"
                    value={formData.echeance}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="priorite">Priorité</label>
                  <select
                    id="priorite"
                    name="priorite"
                    value={formData.priorite}
                    onChange={handleInputChange}
                  >
                    {priorites.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="categorie">Catégorie</label>
                  <select
                    id="categorie"
                    name="categorie"
                    value={formData.categorie}
                    onChange={handleInputChange}
                  >
                    {categories.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                {editingTask && (
                  <div className="form-group">
                    <label htmlFor="statut">Statut</label>
                    <select
                      id="statut"
                      name="statut"
                      value={formData.statut}
                      onChange={handleInputChange}
                    >
                      {statuts.filter(s => s.value !== 'en_retard').map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-annuler" onClick={fermerModal}>
                Annuler
              </button>
              <button className="btn-creer" onClick={sauvegarderTache}>
                {editingTask ? 'Modifier' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete.visible && (
        <div className="confirm-overlay" onClick={handleAnnulerSuppression}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <i className="fas fa-exclamation-circle"></i>
            </div>
            <h4>Supprimer cette tâche ?</h4>
            <p>
              {confirmDelete.task?.titre ? (
                <>
                  Vous êtes sur le point de supprimer la tâche{' '}
                  <strong>{confirmDelete.task.titre}</strong>. Cette action est définitive.
                </>
              ) : (
                'Cette action est définitive.'
              )}
            </p>
            <div className="confirm-actions">
              <button className="btn-annuler" onClick={handleAnnulerSuppression}>
                Annuler
              </button>
              <button className="btn-confirmer" onClick={handleSupprimerTache}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MesTaches;